import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
  ArrowRight,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading"); // loading, success, error, expired
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setStatus("no_token");
    }
  }, [token]);

  const verifyEmail = async (verifyToken) => {
    try {
      const res = await axios.post(`${API}/auth/verify-email`, { token: verifyToken });
      setStatus("success");
      setMessage(res.data.message);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Verification failed";
      setMessage(errorMsg);
      if (errorMsg.includes("expired")) {
        setStatus("expired");
      } else {
        setStatus("error");
      }
    }
  };

  const resendVerification = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success("Verification email sent! Please check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send verification email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <Card className="bg-[#0A0A0A] border-white/10 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-[#F7931A]/20 rounded-full flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-[#F7931A] animate-spin" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                Verifying your email...
              </h2>
              <p className="text-[#94A3B8]">
                Please wait a moment
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                Email Verified! ✅
              </h2>
              <p className="text-[#94A3B8] mb-6">
                Your email has been verified successfully. You can now access all features of your account.
              </p>
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-[#FF6B00] to-[#F7931A] text-white hover:from-[#FF8C00] hover:to-[#FFCD00]">
                  Continue to Login
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                Verification Failed
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {message || "The verification link is invalid."}
              </p>
              <div className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="Enter your email to resend"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black/50 border-white/10 text-white"
                  />
                </div>
                <Button 
                  onClick={resendVerification}
                  disabled={resending}
                  className="w-full bg-gradient-to-r from-[#FF6B00] to-[#F7931A] text-white"
                >
                  {resending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Resend Verification Email
                </Button>
              </div>
            </>
          )}

          {status === "expired" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <Mail className="w-10 h-10 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                Link Expired ⏰
              </h2>
              <p className="text-[#94A3B8] mb-6">
                This verification link has expired. Please request a new one.
              </p>
              <div className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black/50 border-white/10 text-white"
                  />
                </div>
                <Button 
                  onClick={resendVerification}
                  disabled={resending}
                  className="w-full bg-gradient-to-r from-[#FF6B00] to-[#F7931A] text-white"
                >
                  {resending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Send New Verification Link
                </Button>
              </div>
            </>
          )}

          {status === "no_token" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-[#F7931A]/20 rounded-full flex items-center justify-center">
                <Mail className="w-10 h-10 text-[#F7931A]" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                Verify Your Email
              </h2>
              <p className="text-[#94A3B8] mb-6">
                Enter your email address to receive a verification link.
              </p>
              <div className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black/50 border-white/10 text-white"
                  />
                </div>
                <Button 
                  onClick={resendVerification}
                  disabled={resending}
                  className="w-full bg-gradient-to-r from-[#FF6B00] to-[#F7931A] text-white"
                >
                  {resending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Send Verification Email
                </Button>
              </div>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-white/10">
            <Link to="/" className="text-[#94A3B8] hover:text-white text-sm">
              ← Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
